import { FC } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { createUseStyles } from 'react-jss'
import { useTranslation } from 'react-i18next'
import { addDays, format } from 'date-fns'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import './gridStyle.css'
import {
  isDailyEarningTransactionType,
  isRedemptionRequestTransactionType,
  RedemptionRequestStatus,
  TransactionType,
} from 'shared/src/api/types'
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community'
import { useNavigate } from 'react-router-dom'
import cx from 'classnames'
import { formatPriceUsd } from 'shared/src/utils/formatting'

import { SANS_SERIF_FONT_FAMILY } from 'components/styles'

type Row = {
  amount: number
  creatorEarningOrRedemptionRequestId: number
  date: string
  transactionType: TransactionType
}
interface Props {
  rowData: Row[]
  userId: number | undefined
}

const statusColor = (displayText: string | undefined) => {
  switch (displayText) {
    case 'pending':
      return '#E79740'
    case 'fulfilled':
      return '#4E5DD3'
    case 'error':
      return '#F57561'
    case 'bonus':
      return '#3D9050'
    case 'partnership':
      return '#D281F0'
    default:
      return '#454545'
  }
}

export const getRedemptionDisplayStatus = (
  redemptionStatus: RedemptionRequestStatus
) => {
  switch (redemptionStatus) {
    case 'unprocessed':
      return 'pending'
    case 'processing':
      return 'pending'
    default:
      return redemptionStatus
  }
}

const useStyles = createUseStyles({
  container: {
    height: '85dvh',
    maxWidth: 'min(428px, 100%)',
  },
  badgeText: {
    color: 'white',
    fontSize: '10px',
    letterSpacing: '0.72px',
    padding: '2px 8px 2px 8px',
  },
  date: {
    display: 'flex',
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 500,
    letterSpacing: 0.2,
  },
  amount: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 800,
    letterSpacing: 0.2,
  },
})

const formatDate = (date: string) =>
  format(addDays(new Date(date), 1), 'MM.dd.yyyy')

const CreatorFundsGrid: FC<Props> = ({ rowData, userId }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const navigationHandler = (id: number) => {
    navigate(
      userId
        ? `/creatorEarnings/user/${userId}/redeem/${id}`
        : `/earnings/redeem/${id}`
    )
  }

  const filteredRowData = rowData
    .map((row) => ({
      ...row,
      formattedAmount: formatPriceUsd(row.amount),
      date: new Date(row.date), // Convert date string to Date object
    }))
    .filter((row) => parseFloat(row.formattedAmount.slice(1)) > 0)

  const columnDefs: ColDef[] = [
    {
      headerName: t('admin.creatorFund.date'),
      sortable: true,
      sort: 'desc',
      field: 'date',
      cellRenderer: 'badgeCellRenderer',
    },
    {
      headerName: t('admin.creatorFund.value'),
      field: 'amount',
      sortable: true,
      cellRenderer: 'amountRenderer',
      type: 'rightAligned',
    },
  ]

  const getBadge = (displayText: string) => (
    <div
      style={{
        marginLeft: '22px',
        width: '70px',
        borderRadius: '2px',
        height: '18px',
        background: statusColor(displayText ?? ''),
        display: 'flex',
        marginTop: '10px',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <span className={classes.badgeText}>{displayText}</span>
    </div>
  )

  const BadgeCellRenderer: FC<{ data: Row }> = ({ data }) => {
    const rowData: Row = data
    let displayText = ''
    const formattedDate = formatDate(rowData.date)

    if (isRedemptionRequestTransactionType(rowData.transactionType)) {
      displayText = getRedemptionDisplayStatus(rowData.transactionType.value)
      return (
        <div
          style={{ cursor: 'pointer' }}
          className={classes.date}
          onClick={() =>
            navigationHandler(rowData.creatorEarningOrRedemptionRequestId)
          }
        >
          <span style={{ color: statusColor(displayText) }}>
            {formattedDate}
          </span>
          {displayText && getBadge(displayText)}
        </div>
      )
    } else if (!isDailyEarningTransactionType(rowData.transactionType)) {
      displayText = rowData.transactionType.type
    }

    return (
      <div className={classes.date}>
        <span style={{ color: statusColor(displayText) }}>{formattedDate}</span>
        {displayText && getBadge(displayText)}
      </div>
    )
  }

  const AmountCellRenderer: FC<{ data: Row; value: number }> = ({
    data,
    value,
  }) => {
    const rowData: Row = data
    if (isRedemptionRequestTransactionType(rowData.transactionType)) {
      return (
        <div style={{ color: '#90734F' }} className={classes.amount}>
          -{formatPriceUsd(value)}
        </div>
      )
    }
    return <div className={classes.amount}>{formatPriceUsd(value)}</div>
  }

  const gridOptions: GridOptions = {
    onGridReady: (event: GridReadyEvent) => event.api?.sizeColumnsToFit(),
  }

  return (
    <div
      className={cx('ag-theme-alpine', classes.container)}
      id={'creatorEarningsTable'}
    >
      <AgGridReact
        rowData={filteredRowData}
        columnDefs={columnDefs}
        components={{
          badgeCellRenderer: BadgeCellRenderer,
          amountRenderer: AmountCellRenderer,
        }}
        gridOptions={gridOptions}
      ></AgGridReact>
    </div>
  )
}

export default CreatorFundsGrid
